import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface HeaderData {
  email: string;
  companyName: string;
  companyLogo: string;
}

interface UIState {
  isLoading: boolean;
  notifications: Notification[];
  modals: {
    uploadModal: boolean;
    confirmModal: boolean;
  };
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  profileImageUrl: string;
  profileName: string;
  headerData: HeaderData;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

// Load persisted headerData from localStorage
const loadHeaderData = (): HeaderData => {
  try {
    const stored = localStorage.getItem('header_data');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        email: parsed.email || '',
        companyName: parsed.companyName || '',
        companyLogo: parsed.companyLogo || '',
      };
    }
  } catch {
    // ignore parse errors
  }
  return { email: '', companyName: '', companyLogo: '' };
};

const initialState: UIState = {
  isLoading: false,
  notifications: [],
  modals: {
    uploadModal: false,
    confirmModal: false,
  },
  theme: 'light',
  sidebarOpen: false,
  profileImageUrl: '',
  profileName: '',
  headerData: loadHeaderData(),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    openModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = false;
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setProfileImageUrl: (state, action: PayloadAction<string>) => {
      state.profileImageUrl = action.payload;
    },
    setProfileName: (state, action: PayloadAction<string>) => {
      state.profileName = action.payload;
    },
    updateHeaderData: (
      state,
      action: PayloadAction<{
        email?: string;
        companyName?: string;
        companyLogo?: string;
        source: 'Login' | 'Onboarding' | 'Business Verification' | 'Profile/Media';
      }>
    ) => {
      const { email, companyName, companyLogo, source } = action.payload;
      const updated: Partial<HeaderData> = {};

      // Only update fields when the new value is a non-empty string
      if (email && email.trim()) {
        state.headerData.email = email.trim();
        updated.email = email.trim();
      }
      if (companyName && companyName.trim()) {
        state.headerData.companyName = companyName.trim();
        updated.companyName = companyName.trim();
      }
      if (companyLogo && companyLogo.trim()) {
        state.headerData.companyLogo = companyLogo.trim();
        updated.companyLogo = companyLogo.trim();
      }

      if (Object.keys(updated).length > 0) {
        console.log(`[headerData] Updated from ${source}:`, {
          email: state.headerData.email,
          companyName: state.headerData.companyName,
          companyLogo: state.headerData.companyLogo,
        });
        // Persist to localStorage
        try {
          localStorage.setItem('header_data', JSON.stringify({
            email: state.headerData.email,
            companyName: state.headerData.companyName,
            companyLogo: state.headerData.companyLogo,
          }));
        } catch {
          // ignore storage errors
        }
      }
    },
  },
});

export const {
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  toggleTheme,
  setTheme,
  toggleSidebar,
  setSidebarOpen,
  setProfileImageUrl,
  setProfileName,
  updateHeaderData,
} = uiSlice.actions;

export default uiSlice.reducer;